import type { ComponentProps } from 'react';
import {
  type FieldPath,
  type FieldValues,
  type FormState,
  get,
  type RegisterOptions,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import InputWarningText from '@/components/InputWarningText';
import { Input } from '@/ui-kit/components/Input/Input';
import { isValidEmail, suggestCorrectEmail } from '@/utils/validate';

const SuggestionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  color: inherit;

  /* Increase clickable area without changing the size of the button */
  padding: 6px;
  margin: -6px;
`;

type Props<TFieldValues extends FieldValues> = ComponentProps<typeof Input> & {
  register: UseFormRegister<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  setValue: (email: string) => void;
  formState: FormState<TFieldValues>;
  name: FieldPath<TFieldValues>;
  registerOptions?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;
  required?: boolean;
};

const EmailInput = <TFieldValues extends FieldValues>({
  register,
  watch,
  setValue,
  formState,
  name,
  registerOptions = {},
  required,
  ...restProps
}: Props<TFieldValues>) => {
  const { t } = useTranslation('FormErrors');
  const { errors, dirtyFields } = formState;

  const email = watch(name);
  const isValid = !!get(dirtyFields, name) && !errors[name];
  const emailSuggestion = isValid ? suggestCorrectEmail(email) : undefined;

  return (
    <div>
      <Input
        data-private
        {...register(name, {
          validate: (value: string) => isValidEmail(value) || t('invalidEmail'),
          maxLength: { value: 128, message: t('maxLength', { length: 128 }) },
          ...(required ? { required: t('required') } : {}),
          ...registerOptions,
        })}
        type="text"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        errorMessage={errors[name]?.message as string | undefined}
        {...restProps}
      />
      <InputWarningText data-testid="email-typo-suggestion" aria-live="polite">
        {!!emailSuggestion && (
          // Own keyed element: browser translators swap text nodes for <font>, so React may only remove or replace whole elements here.
          <span key={emailSuggestion}>
            <Trans
              i18nKey="FormErrors:emailTypo"
              components={{
                suggestion: (
                  <SuggestionButton
                    data-private
                    type="button"
                    onClick={() => {
                      setValue(emailSuggestion);
                    }}
                  />
                ),
              }}
              values={{ suggestion: emailSuggestion }}
            />
          </span>
        )}
      </InputWarningText>
    </div>
  );
};

export default EmailInput;
